import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom-v5-compat';

import PersonaErrorBoundary from 'dashboard/components/PersonaErrorBoundary';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Document, DocumentGeneric, DocumentGovernmentId } from 'dashboard/models/document';
import DocumentGenericViewer from 'dashboard/views/DashboardDocumentViewerView/DocumentGenericViewer';
import { DocumentGovernmentIdViewer } from 'dashboard/views/DashboardDocumentViewerView/DocumentGovernmentIdViewer';
import { ApiModel } from 'lib/interfaces/api';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';
import { captureMessage } from 'lib/reporting';

interface NativeDocumentViewerProps {
  document: DocumentGeneric;
}

const NativeDocumentViewer = ({ document }: NativeDocumentViewerProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [filesOpened, setFilesOpened] = useState(false);

  useEffect(() => {
    const { files, 'files-normalized': filesNormalized } = document.attributes;
    const displayFiles = files.length === filesNormalized.length ? filesNormalized : files;

    displayFiles.forEach((file, index) => {
      window.open(file.url, '_blank', 'noopener,noreferrer');
      if (index === displayFiles.length - 1) {
        setFilesOpened(true);
      }
    });
  }, [document]);

  useEffect(() => {
    if (filesOpened) {
      if (location.state?.internalRedirect) {
        navigate(-1);
      } else {
        navigate('/dashboard/inquiries');
      }
    }
  }, [document.attributes.files.length, filesOpened, location.state, navigate]);

  return null;
};

export interface DashboardDocumentViewerViewProps {
  document: Document;
  featureFlags: FeatureFlagsMap;
  fileLoading: boolean;
  rotation: number;
  resetBaseState: () => void;
  rotateDocument: () => void;
  setZoomScale: (zoomScale: number) => void;
  zoomScale: number;
  showMessage: (message: string, messageType: MessageType) => void;
  nullStateMessage: string;
  nullStateRedirect: string;
  incrementZoomScale: () => void;
  decrementZoomScale: () => void;
  zoomVisualValue: string;
  setZoomVisualValue: (zoomVisualValue: string) => void;
}

const DashboardDocumentViewerView = ({
  document,
  featureFlags,
  fileLoading,
  resetBaseState,
  rotateDocument,
  rotation,
  setZoomScale,
  zoomScale,
  nullStateMessage,
  nullStateRedirect,
  incrementZoomScale,
  decrementZoomScale,
  zoomVisualValue,
  setZoomVisualValue,
  showMessage,
}: DashboardDocumentViewerViewProps) => {
  useEffect(() => {
    // clean up state when component is unmounting
    return () => resetBaseState();
  }, []);

  if (document?.type === ApiModel.DocumentGeneric) {
    return (
      <PersonaErrorBoundary
        fallback={() => <NativeDocumentViewer document={document} />}
        onCatch={(error) =>
          captureMessage('Error when rendering DocumentGenericViewer', 'error', {
            error,
            component: 'DashboardDocumentViewerView',
          })
        }
      >
        <DocumentGenericViewer
          document={document}
          featureFlags={featureFlags}
          showMessage={showMessage}
          fileLoading={fileLoading}
          rotateDocument={rotateDocument}
          rotation={rotation}
          setZoomScale={setZoomScale}
          zoomScale={zoomScale}
          nullStateMessage={nullStateMessage}
          nullStateRedirect={nullStateRedirect}
          incrementZoomScale={incrementZoomScale}
          decrementZoomScale={decrementZoomScale}
          zoomVisualValue={zoomVisualValue}
          setZoomVisualValue={setZoomVisualValue}
        />
      </PersonaErrorBoundary>
    );
  } else if (document?.type === ApiModel.DocumentGovernmentId) {
    return (
      <DocumentGovernmentIdViewer
        document={document as DocumentGovernmentId}
        featureFlags={featureFlags}
        showMessage={showMessage}
        fileLoading={fileLoading}
        rotateDocument={rotateDocument}
        rotation={rotation}
        setZoomScale={setZoomScale}
        zoomScale={zoomScale}
        nullStateMessage={nullStateMessage}
        nullStateRedirect={nullStateRedirect}
        incrementZoomScale={incrementZoomScale}
        decrementZoomScale={decrementZoomScale}
        zoomVisualValue={zoomVisualValue}
        setZoomVisualValue={setZoomVisualValue}
      />
    );
  } else {
    return null;
  }
};
export default DashboardDocumentViewerView;
